import { useQuery } from '@tanstack/react-query'
import { z } from 'zod'
import { useApi } from '@/lib/auth'
import { usePreviewData } from '../preview-data'
import { useLocation } from './use-location'

// GET /v1/network-status?code=&banner=  (envelope: { code, success, data: { ...payload } })
const networkStatusPayloadSchema = z.object({
  success: z.boolean(),
  message: z.string().optional(),
  /** device name -> status string ("UP"/"DOWN"/...) */
  data: z.record(z.string(), z.string()),
  debug: z
    .array(
      z.object({
        kind: z.string(),
        manufacturer: z.string(),
        externalId: z.string().nullable(),
        serial: z.string().nullable(),
        hostname: z.string().nullable(),
        status: z.string(),
      }),
    )
    .optional(),
  locationStatus: z.enum(['UP', 'DOWN', 'DEGRADED']).optional(),
  tunnels: z
    .object({
      cloudflare: z.record(z.string(), z.string()).optional(),
      meraki_s2s: z.record(z.string(), z.string()).optional(),
    })
    .optional(),
  backbone: z.record(z.string(), z.string()).optional(),
  lastPolledAt: z.string().nullable().optional(),
  wanInterfaces: z
    .array(
      z.object({
        interfaceName: z.string(),
        vendor: z.string(),
        wanType: z.string().nullable(),
        decision: z.string(),
        vendorStatus: z.string(),
        lastPolledAt: z.string(),
      }),
    )
    .optional(), // absent on very old payloads; treat as [] (no zod .default: keeps input/output types equal)
})
const networkStatusEnvelopeSchema = z.object({
  success: z.boolean(),
  code: z.union([z.string(), z.number()]).optional(),
  data: networkStatusPayloadSchema,
})
export type NetworkStatusPayload = z.infer<typeof networkStatusPayloadSchema>

// GET /v1/tunnel-status?storeCode=  (envelope: { code, success, data })
const tunnelStatusEnvelopeSchema = z.object({
  success: z.boolean(),
  code: z.union([z.string(), z.number()]).optional(),
  data: z.object({
    tunnelStatus: z.string(),
    deviceStatus: z.string().optional(),
    vpnMode: z.string().optional(),
    peers: z
      .object({
        meraki: z.array(z.object({ networkId: z.string(), networkName: z.string(), reachability: z.string() })),
        thirdParty: z.array(z.object({ name: z.string(), publicIp: z.string(), reachability: z.string() })),
      })
      .optional(),
  }),
})
export type TunnelStatusPayload = z.infer<typeof tunnelStatusEnvelopeSchema>['data']

export interface DeviceStatusSummary {
  total: number
  up: number
  down: number
  other: number
}

/** Pure: roll a device-name -> status record into UP/DOWN/other counts (case-insensitive). */
export function summarizeDeviceStatuses(devices: Record<string, string>): DeviceStatusSummary {
  const summary: DeviceStatusSummary = { total: 0, up: 0, down: 0, other: 0 }
  for (const status of Object.values(devices)) {
    summary.total += 1
    const s = status.toUpperCase()
    if (s === 'UP' || s === 'ONLINE') summary.up += 1
    else if (s === 'DOWN' || s === 'OFFLINE') summary.down += 1
    else summary.other += 1
  }
  return summary
}

export interface NetworkOverview {
  status: NetworkStatusPayload
  /** `null` when the tunnel-status call is unavailable/failed (404 for stores with no VPN data). */
  tunnel: TunnelStatusPayload | null
  summary: DeviceStatusSummary
}

/**
 * API-backed network overview: current device status + tunnel status for a store.
 * Resolves the store's aptos code and banner via `useLocation` (shared cache), then fetches in parallel.
 * Network HISTORY (poll charts, VLAN counts, drift) is direct-Neon: see `useNetworkHistory` (deferred).
 */
export function useNetworkOverview(idOrCode: string) {
  const api = useApi()
  const preview = usePreviewData()
  const location = useLocation(idOrCode)
  const code = location.data?.aptosStoreCode ?? null
  const banner = location.data?.banner ?? null
  return useQuery({
    queryKey: preview ? ['preview', 'network-overview'] : ['network-overview', code, banner],
    queryFn: async (): Promise<NetworkOverview> => {
      if (!code || !banner) throw new Error('Location has no store code/banner')
      const [statusRes, tunnel] = await Promise.all([
        api.get(
          `/v1/network-status?code=${encodeURIComponent(code)}&banner=${encodeURIComponent(banner.toLowerCase())}`,
          networkStatusEnvelopeSchema,
        ),
        api.get(`/v1/tunnel-status?storeCode=${encodeURIComponent(code)}`, tunnelStatusEnvelopeSchema).then(
          (r) => (r.success ? r.data : null),
          () => null, // tunnel data is optional; the page still renders device status without it
        ),
      ])
      if (!statusRes.success) throw new Error(`Failed to fetch network status (code ${statusRes.code ?? 'unknown'})`)
      return { status: statusRes.data, tunnel, summary: summarizeDeviceStatuses(statusRes.data.data) }
    },
    enabled: !preview && code !== null && banner !== null,
    ...(preview ? { initialData: preview.networkOverview, staleTime: Number.POSITIVE_INFINITY } : {}),
  })
}

/**
 * Network history (device/tunnel poll buckets, VLAN client counts, drift severity).
 * TODO(BFF-parity wave): network history is direct-Neon (`lib/network-overview.server.ts` in v1);
 * needs new sync-app-api endpoints. Designs should render a "coming soon" panel while `deferred`.
 */
export function useNetworkHistory(_idOrCode: string, _range?: string): { deferred: true } {
  return { deferred: true }
}
